import { useState, useEffect } from 'react'
import { logger } from '@/utils/logger'
import type { VolunteerSlot } from '../types/slot'

interface ClassFilterChipsProps {
  slots: VolunteerSlot[]
  selectedClasses: string[]
  onSelectionChange: (classes: string[]) => void
}

const removeClassPrefix = (className: string) => {
  return className.replace('כיתה ', '')
}

export function ClassFilterChips({ slots, selectedClasses, onSelectionChange }: ClassFilterChipsProps) {
  const [availableClasses, setAvailableClasses] = useState<string[]>([])

  useEffect(() => {
    const classes = new Set<string>()
    
    slots.forEach(slot => {
      if (slot.class_name && slot.class_name !== 'פתוח') {
        classes.add(slot.class_name)
      }
    })
    
    const sortedClasses = Array.from(classes).sort((a, b) => {
      const extractGradeAndClass = (className: string) => {
        const cleaned = className.replace('כיתה ', '')
        const match = cleaned.match(/([א-ת])-(\d+)/)
        if (match) {
          const hebrewGrades = ['א', 'ב', 'ג', 'ד', 'ה', 'ו']
          const gradeIndex = hebrewGrades.indexOf(match[1])
          const classNum = parseInt(match[2])
          return [gradeIndex, classNum]
        }
        return [999, 999]
      }
      
      const [gradeA, classA] = extractGradeAndClass(a)
      const [gradeB, classB] = extractGradeAndClass(b)
      
      if (gradeA !== gradeB) return gradeA - gradeB
      return classA - classB
    })
    
    setAvailableClasses(sortedClasses)
  }, [slots, selectedClasses])

  const handleChipClick = (className: string) => {
    let newSelection: string[]
    
    if (selectedClasses.includes(className)) {
      if (selectedClasses.length === 1) {
        newSelection = []
      } else {
        newSelection = selectedClasses.filter(c => c !== className)
      }
    } else {
      newSelection = [...selectedClasses, className]
    }
    
    onSelectionChange(newSelection)
  }

  const handleSelectAll = () => {
    if (selectedClasses.length === availableClasses.length) {
      onSelectionChange([])
    } else {
      onSelectionChange([...availableClasses])
    }
  }

  const getFilterSummary = () => {
    if (selectedClasses.length === 0) {
      return 'הצג הכל'
    } else if (selectedClasses.length === availableClasses.length) {
      return 'כל הכיתות'
    } else if (selectedClasses.length === 1) {
      return `כיתה ${removeClassPrefix(selectedClasses[0])}`
    } else {
      return `${selectedClasses.length} כיתות`
    }
  }

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-200" dir="rtl">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
            </svg>
            <h3 className="text-lg font-bold text-gray-900 font-hebrew">
              סינון לפי כיתות
            </h3>
          </div>
          
          <button
            onClick={handleSelectAll}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1 rounded-full transition-colors font-hebrew"
          >
            {selectedClasses.length === availableClasses.length ? 'נקה הכל' : 'בחר הכל'}
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600 font-hebrew">
              <span>מציג:</span>
              <span className="font-bold text-blue-600">
                {getFilterSummary()}
              </span>
              {selectedClasses.length > 0 && (
                <span className="text-gray-500">
                  ({selectedClasses.length}/{availableClasses.length})
                </span>
              )}
            </div>
          </div>
          
          {selectedClasses.length > 0 && (
            <div className="text-xs text-gray-500 font-hebrew italic">
              💡 ניתן לשתף את הקישור עם הסינון הנוכחי עם הורים אחרים
            </div>
          )}
          
          <div className="flex items-center gap-2">
            {selectedClasses.length > 0 && (
              <button
                onClick={async (event) => {
                  const url = window.location.href
                  try {
                    if (navigator.share) {
                      await navigator.share({
                        title: `סינון כיתות: ${selectedClasses.map(c => c.replace('כיתה ', '')).join(', ')}`,
                        text: 'תורנות הורים - בית ספר אילנות',
                        url: url
                      })
                    } else {
                      await navigator.clipboard.writeText(url)
                      const button = event?.target as HTMLElement
                      const originalText = button?.textContent
                      if (button) {
                        button.textContent = 'הועתק!'
                        setTimeout(() => {
                          button.textContent = originalText
                        }, 2000)
                      }
                    }
                  } catch (error) {
                    console.warn('Failed to share:', error)
                  }
                }}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded-full transition-colors font-hebrew"
                title="שתף קישור עם הסינון"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                שתף
              </button>
            )}
            
            {selectedClasses.length > 0 && (
              <button
                onClick={() => onSelectionChange([])}
                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded-full transition-colors font-hebrew"
                title="נקה את כל הסינונים"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                נקה סינון
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {availableClasses.length === 0 ? (
            <div className="text-sm text-gray-600 font-hebrew italic">
              אין כיתות זמינות לסינון כרגע...
            </div>
          ) : (
            availableClasses.map(className => {
              const isSelected = selectedClasses.includes(className)
              
              return (
                <button
                  key={className}
                  onClick={() => handleChipClick(className)}
                  className={`
                    inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200
                    min-h-[44px] touch-manipulation font-hebrew
                    ${isSelected
                      ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-md hover:bg-blue-700 transform scale-105'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50'
                    }
                  `}
                  title={`${isSelected ? 'הסר' : 'הוסף'} כיתה ${removeClassPrefix(className)}`}
                >
                  <div className={`
                    w-4 h-4 rounded border-2 flex items-center justify-center transition-colors
                    ${isSelected 
                      ? 'bg-white border-white' 
                      : 'bg-transparent border-current'
                    }
                  `}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  
                  <span>כיתה {removeClassPrefix(className)}</span>
                </button>
              )
            })
          )}
        </div>

        {availableClasses.length > 3 && (
          <div className="flex gap-2 pt-2 border-t border-gray-200">
            <button
              onClick={() => {
                const gradeAClasses = availableClasses.filter(c => c.includes('כיתה א-'))
                onSelectionChange(gradeAClasses)
              }}
              className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors font-hebrew font-medium"
            >
              כיתות א
            </button>
            <button
              onClick={() => {
                const gradeBClasses = availableClasses.filter(c => c.includes('כיתה ב-'))
                onSelectionChange(gradeBClasses)
              }}
              className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors font-hebrew font-medium"
            >
              כיתות ב
            </button>
            <button
              onClick={() => {
                const gradeGClasses = availableClasses.filter(c => c.includes('כיתה ג-'))
                onSelectionChange(gradeGClasses)
              }}
              className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors font-hebrew font-medium"
            >
              כיתות ג
            </button>
            <button
              onClick={() => {
                const gradeDClasses = availableClasses.filter(c => c.includes('כיתה ד-'))
                onSelectionChange(gradeDClasses)
              }}
              className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors font-hebrew font-medium"
            >
              כיתות ד
            </button>
            <button
              onClick={() => {
                const gradeHClasses = availableClasses.filter(c => c.includes('כיתה ה-'))
                onSelectionChange(gradeHClasses)
              }}
              className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition-colors font-hebrew font-medium"
            >
              כיתות ה
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
